import { Button } from "@/components/ui/button";
import Icons from "../../icons";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useNavigate } from "react-router-dom";

export const CreateChannel = ({ children }: { children: React.ReactNode }) => {
    const { teamId } = useParams();
    const navigate = useNavigate();
    const [buttonDisabled, setButtonDisabled] = useState(true);
    const [onOpen, setOnOpen] = useState(false);
    const [channelName, setChannelName] = useState("");
    const [channelNameError, setChannelNameError] = useState("");
    const [channelType, setChannelType] = useState("text");

    const [addiontalData, setAddiontalData] = useState({
        streamChannel: "",
        streamPlatform: "twitch",
    });

    useEffect(() => {
        const channelNameStr = String(channelName);
    
        if (
            (channelNameStr.trim()).length === 0 ||
            (channelNameStr.trim()).length > 20 ||
            (channelType === "watchstream" && addiontalData.streamChannel.trim() === "")
            ||
            (channelType === "watchstream" && addiontalData.streamChannel.length < 4) ||
            (channelType === "watchstream" && addiontalData.streamChannel.length > 25)
        ) {
            setButtonDisabled(true);
        } else {
            setButtonDisabled(false);
        }
    }, [channelName, channelType, addiontalData.streamChannel]);
    
    const createChannel = async () => {
        setButtonDisabled(true);
        const response = await fetch(`__SITE_BASE__/api/v1/teams/${teamId}/channels`, {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                name: channelName,
                type: channelType,
                parentId: null,
                additionalData: channelType === "watchstream" ? addiontalData : {},
            }),
        });
        const data = await response.json();

        if (data.message) {
            setButtonDisabled(false);
            return setChannelNameError(data.message);
        }
        setOnOpen(false);
        navigate(`/teams/${teamId}/channels/${data.channel.id}`);
        setChannelName("");
        setChannelType("text");
        setAddiontalData({
            streamChannel: "",
            streamPlatform: "twitch",
        });
    };

    return (
        <Dialog open={onOpen} onOpenChange={(onOpen) => setOnOpen(onOpen)}>
            <DialogTrigger>{children}</DialogTrigger>
            <DialogContent className="bg-brand-dark text-white border-0 ">
                <DialogHeader className="items-start justify-start">
                    <DialogTitle>Create Channel</DialogTitle>
                    <DialogDescription />
                </DialogHeader>

                <Label className="text-gray-300">Channel Type</Label>
                <div className="flex flex-col items-start gap-2">
                    <div className="flex flex-col w-full gap-2 bg-brand-darkest p-2 rounded-lg">
                        {[
                            {
                                id: "text",
                                name: "Chat",
                                description: "A chat channel is a place for focused conversations",
                                icon: "hashtag",
                                value: "text",
                            },
                            {
                                id: "todo",
                                name: "Todo",
                                description: "A todo channel for managing tasks and deadlines",
                                icon: "todo",
                                value: "todo",
                            },
                            {
                                id: "watchstream",
                                name: "Watch Stream",
                                description: "A watch stream channel for watching videos together",
                                icon: "stream",
                                value: "watchstream",
                            },
                            {
                                id: "voice",
                                name: "Voice",
                                description: "A voice channel for voice conversations",
                                icon: "volume",
                                isBeta: true,
                                value: "voice",
                            }
                        ].map((type) => (
                            <div
                                key={type.id}
                                className={`flex items-center w-full gap-2 p-2 rounded-lg transition ${
                                    channelType === type.value
                                        ? "bg-brand-darker"
                                        : "bg-brand-darkest hover:bg-brand-darker"
                                }`}
                            >
                                <label
                                    htmlFor={type.id}
                                    className="flex w-full items-center justify-between cursor-pointer"
                                >
                                    <div className="flex items-center">
                                        <Icons name={type.icon} size="24" color="white" />
                                        <div className="flex flex-col ml-2">
                                            <div className="flex items-center gap-2">
                                                <span className="text-white">{type.name}</span>
                                                {type?.isBeta && (
                                                    <span className="text-xs bg-brand-green text-white px-1 rounded-lg">
                                                        Beta
                                                    </span>
                                                )}
                                            </div>
                                            <p className="text-gray-300 text-sm">{type.description}</p>
                                        </div>
                                    </div>
                                    <div className="relative flex items-center justify-center w-5 h-5">
                                        <input
                                            type="radio"
                                            id={type.id}
                                            name="channelType"
                                            value={type.value}
                                            checked={channelType === type.value}
                                            onChange={(e) => setChannelType(e.target.value)}
                                            className="absolute opacity-0 w-full h-full cursor-pointer"
                                        />
                                        <div
                                            className={`w-4 h-4 rounded-full border-2 transition ${
                                                channelType === type.value
                                                    ? "bg-brand-green border-brand-green"
                                                    : "border-gray-300"
                                            }`}
                                        />
                                    </div>
                                </label>
                            </div>
                        ))}
                    </div>
                </div>

                <Label className="text-gray-300">Channel Name</Label>
                {channelNameError && <p className="text-red-500 text-sm">{channelNameError}</p>}

                <Input
                    className="rounded-lg bg-brand-darkest border-r-0 border-l-0 border-t-0 border-b-brand-green placeholder:text-gray-300"
                    placeholder="Channel Name"
                    type="text"
                    autoComplete="false"
                    onChange={(e) => setChannelName(e.target.value)}
                />

                {channelType === "watchstream" && (
                    <>
                        <Label className="text-gray-300">
                            Twitch Username
                        </Label>
                        <Input
                            className="rounded-lg bg-brand-darkest border-r-0 border-l-0 border-t-0 border-b-brand-green placeholder:text-gray-300"
                            placeholder="Twitch Username"
                            type="text"
                            autoComplete="false"
                            value={addiontalData.streamChannel}
                            onChange={(e) =>
                                setAddiontalData({
                                    ...addiontalData,
                                    streamChannel: e.target.value,
                                })
                            }
                        />
                    </>
                )}


                <div className="flex items-end justify-end mt-2">
                    <Button
                        className="bg-brand-green px-5 py-2 rounded-lg text-white font-semibold hover:bg-green-500"
                        disabled={buttonDisabled}
                        onClick={createChannel}
                    >
                        Create
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
};
